import React, { useState, useEffect } from 'react';
import SearchBar from '@/components/SearchBar';
import QuickDial from '@/components/QuickDial';
import AIPlatforms from '@/components/AIPlatforms';
import Clock from '@/components/Clock';
import TodoList from '@/components/TodoList';
import TodoTicker from '@/components/TodoTicker';
import { Settings2, Palette, Image as ImageIcon, X, Grid, Activity, Zap, CircleDashed, Upload, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getSettings, saveSettings, getTags } from '@/utils/storage';
import { translations, Language } from '@/utils/i18n';
import { useTheme } from '@/hooks/useTheme';
import { useTodos } from '@/hooks/useTodos';
import { Tag } from '@/types';

const Home = () => {
  const { theme, toggleTheme } = useTheme();
  const todoData = useTodos();
  const { todos } = todoData;
  const [showConfig, setShowConfig] = useState(false);
  const [showSkinConfig, setShowSkinConfig] = useState(false);
  const [visibleComponents, setVisibleComponents] = useState({
    clock: true,
    slogan: true,
    search: true,
    ai: true,
    quickDial: true,
    todo: true
  });
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [backgroundType, setBackgroundType] = useState<'default' | 'image'>('default');
  const [backgroundImage, setBackgroundImage] = useState('');
  const [defaultBackgroundStyle, setDefaultBackgroundStyle] = useState<'orb' | 'grid' | 'rays' | 'flow'>('orb');
  const [isTodoExpanded, setIsTodoExpanded] = useState(true);
  const [todoPosition, setTodoPosition] = useState({ x: 0, y: 0 });
  
  const [language, setLanguage] = useState<Language>('zh-CN');
  const [tags, setTags] = useState<Tag[]>([]);
  const [aiSourceTagId, setAiSourceTagId] = useState<string>('');
  const [quickDialSourceTagId, setQuickDialSourceTagId] = useState<string>('');

  const t = translations[language];

  useEffect(() => {
    const savedPos = localStorage.getItem('todo-position');
    if (savedPos) {
        setTodoPosition(JSON.parse(savedPos));
    }
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: e.clientX,
        y: e.clientY
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    
    // Load settings and tags
    const loadData = async () => {
        const settings = await getSettings();
        if (settings.backgroundImage) setBackgroundImage(settings.backgroundImage);
        if (settings.backgroundType) setBackgroundType(settings.backgroundType);
        if (settings.defaultBackgroundStyle) setDefaultBackgroundStyle(settings.defaultBackgroundStyle);
        if (settings.language) setLanguage(settings.language);
        if (settings.aiSourceTagId) setAiSourceTagId(settings.aiSourceTagId);
        if (settings.quickDialSourceTagId) setQuickDialSourceTagId(settings.quickDialSourceTagId);

        const loadedTags = await getTags();
        setTags(loadedTags);
    };
    loadData();

    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const toggleComponent = (key: keyof typeof visibleComponents) => {
    setVisibleComponents(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
          if (file.size > 5 * 1024 * 1024) { // Limit to 5MB
              alert('图片大小不能超过 5MB');
              return;
          }
          
          const formData = new FormData();
          formData.append('file', file);

          try {
              const response = await fetch('http://localhost:3014/upload', {
                  method: 'POST',
                  body: formData
              });
              
              if (!response.ok) throw new Error('Upload failed');
              
              const data = await response.json();
              handleBgChange(data.url);
          } catch (error) {
              console.error('Upload error:', error);
              alert('上传失败，请确保本地服务已启动 (运行 start-server.bat)');
          }
      }
  };

  const handleBgChange = async (url: string) => {
      setBackgroundImage(url);
      const type = url ? 'image' : 'default';
      setBackgroundType(type);
      
      const settings = await getSettings();
      await saveSettings({
          ...settings,
          backgroundImage: url,
          backgroundType: type
      });
  };

  const handleStyleChange = async (style: 'orb' | 'grid' | 'rays' | 'flow') => {
      setDefaultBackgroundStyle(style);
      setBackgroundType('default');
      setBackgroundImage('');
      
      const settings = await getSettings();
      await saveSettings({
          ...settings,
          backgroundType: 'default',
          backgroundImage: '',
          defaultBackgroundStyle: style
      });
  };

  const handleLanguageChange = async (lang: Language) => {
      setLanguage(lang);
      const settings = await getSettings();
      await saveSettings({ ...settings, language: lang });
  };

  const handleAiSourceChange = async (tagId: string) => {
      setAiSourceTagId(tagId);
      const settings = await getSettings();
      await saveSettings({ ...settings, aiSourceTagId: tagId });
  };

  const handleQuickDialSourceChange = async (tagId: string) => {
      setQuickDialSourceTagId(tagId);
      const settings = await getSettings();
      await saveSettings({ ...settings, quickDialSourceTagId: tagId });
  };

  return (
    <div className="min-h-screen text-slate-800 dark:text-white relative overflow-hidden bg-slate-50 dark:bg-[#0f172a] transition-colors duration-300">
      
      <div className="absolute top-4 left-4 z-50">
      </div>

      {/* Background Layer */}
      {backgroundType === 'image' && backgroundImage ? (
          <div className="fixed inset-0 z-0">
              <img src={backgroundImage} alt="background" className="w-full h-full object-cover object-center" />
              <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"></div>
          </div>
      ) : (
        <>
            {/* Base Background - Adapts to Theme */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none bg-slate-50 dark:bg-[#0f172a] transition-colors duration-300">
                
                {/* Light Mode Base Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50/50 dark:opacity-0 transition-opacity duration-300"></div>

                {/* Style: Orb (Original) */}
                {defaultBackgroundStyle === 'orb' && (
                    <>
                        {/* Dynamic Gradient Orb following mouse */}
                        <div 
                            className="absolute w-[800px] h-[800px] bg-blue-400/10 dark:bg-blue-600/10 rounded-full blur-[120px] transition-transform duration-1000 ease-out"
                            style={{
                                top: mousePosition.y - 400,
                                left: mousePosition.x - 400,
                            }}
                        />
                        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-400/10 dark:bg-purple-600/10 rounded-full blur-[100px]"></div>
                        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-cyan-400/10 dark:bg-cyan-600/10 rounded-full blur-[100px]"></div>
                        <div className="absolute inset-0 opacity-20 dark:bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_100%)]"></div>
                    </>
                )}

                {/* Style: Grid */}
                {defaultBackgroundStyle === 'grid' && (
                    <>
                         <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.1)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_at_center,black_60%,transparent_100%)] perspective-1000 transform-gpu rotate-x-12 scale-110"></div>
                         <div 
                            className="absolute w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[100px]"
                            style={{
                                top: mousePosition.y - 300,
                                left: mousePosition.x - 300,
                            }}
                        />
                    </>
                )}

                {/* Style: Rays */}
                {defaultBackgroundStyle === 'rays' && (
                    <>
                         <div className="absolute inset-0 bg-[conic-gradient(from_0deg_at_50%_50%,rgba(59,130,246,0.1)_0deg,transparent_60deg,rgba(59,130,246,0.1)_120deg,transparent_180deg,rgba(59,130,246,0.1)_240deg,transparent_300deg)] opacity-30 animate-spin-slow"></div>
                         <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/50 to-white dark:via-slate-900/50 dark:to-slate-900"></div>
                         <div 
                            className="absolute w-[400px] h-[400px] bg-purple-500/20 rounded-full blur-[80px]"
                             style={{
                                top: mousePosition.y - 200,
                                left: mousePosition.x - 200,
                            }}
                         />
                    </>
                )}

                {/* Style: Flow */}
                {defaultBackgroundStyle === 'flow' && (
                    <>
                        <div className="absolute inset-0 opacity-30 dark:opacity-40">
                             <div className="absolute top-[-50%] left-[-50%] w-[200%] h-[200%] animate-blob bg-gradient-to-r from-blue-400/30 to-purple-400/30 dark:from-blue-600/30 dark:to-purple-600/30 blur-[100px] rounded-full mix-blend-multiply dark:mix-blend-screen"></div>
                             <div className="absolute top-[-50%] right-[-50%] w-[200%] h-[200%] animate-blob animation-delay-2000 bg-gradient-to-l from-cyan-400/30 to-blue-400/30 dark:from-cyan-600/30 dark:to-blue-600/30 blur-[100px] rounded-full mix-blend-multiply dark:mix-blend-screen"></div>
                             <div className="absolute bottom-[-50%] left-[20%] w-[150%] h-[150%] animate-blob animation-delay-4000 bg-gradient-to-t from-purple-400/30 to-pink-400/30 dark:from-purple-600/30 dark:to-pink-600/30 blur-[100px] rounded-full mix-blend-multiply dark:mix-blend-screen"></div>
                        </div>
                    </>
                )}

                
                {/* Floating Particles (Simulated) - Common for all except Flow */}
                {defaultBackgroundStyle !== 'flow' && (
                    <div className="absolute inset-0">
                        {[...Array(20)].map((_, i) => (
                            <motion.div
                                key={i}
                                className="absolute bg-slate-900/10 dark:bg-white/20 rounded-full"
                                initial={{ 
                                    x: Math.random() * window.innerWidth, 
                                    y: Math.random() * window.innerHeight,
                                    scale: Math.random() * 0.5 + 0.5,
                                    opacity: Math.random() * 0.3 + 0.1
                                }}
                                animate={{ 
                                    y: [null, Math.random() * -100],
                                    opacity: [null, 0]
                                }}
                                transition={{ 
                                    duration: Math.random() * 10 + 10, 
                                    repeat: Infinity,
                                    ease: "linear" 
                                }}
                                style={{
                                    width: Math.random() * 4 + 1,
                                    height: Math.random() * 4 + 1,
                                }}
                            />
                        ))}
                    </div>
                )}
            </div>
        </>
      )}

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 py-8 flex flex-col items-center min-h-screen">
        
        {/* Top Right Controls */}
        <div className="absolute top-4 right-4 z-50 flex gap-2">
            {/* Theme Toggle Button */}
            <button
                onClick={toggleTheme}
                className="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors bg-white/50 dark:bg-white/5 rounded-lg border border-slate-200 dark:border-white/5 hover:bg-white/80 dark:hover:bg-white/10 backdrop-blur-md shadow-sm"
                title={`Theme: ${theme}`}
            >
                {theme === 'light' ? <span className="text-lg">☀️</span> : theme === 'dark' ? <span className="text-lg">🌙</span> : <span className="text-lg">🖥️</span>}
            </button>

            {/* Skin Settings Button */}
            <button 
                onClick={() => { setShowSkinConfig(!showSkinConfig); setShowConfig(false); }}
                className="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors bg-white/50 dark:bg-white/5 rounded-lg border border-slate-200 dark:border-white/5 hover:bg-white/80 dark:hover:bg-white/10 backdrop-blur-md shadow-sm"
            >
                <Palette size={20} />
            </button>

            {/* General Settings Button */}
            <button 
                onClick={() => { setShowConfig(!showConfig); setShowSkinConfig(false); }}
                className="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors bg-white/50 dark:bg-white/5 rounded-lg border border-slate-200 dark:border-white/5 hover:bg-white/80 dark:hover:bg-white/10 backdrop-blur-md shadow-sm"
            >
                <Settings2 size={20} />
            </button>
            
            {/* Config Menu */}
            <AnimatePresence>
                {showConfig && (
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.9, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 10 }}
                        className="absolute right-0 top-12 mt-2 w-64 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-xl shadow-2xl p-4 z-50 overflow-y-auto max-h-[80vh]"
                    >
                        {/* Language Selector */}
                         <div className="mb-4">
                            <label className="text-xs text-slate-500 mb-1.5 block font-medium flex items-center gap-1"><Globe size={12}/> Language</label>
                            <select 
                                value={language} 
                                onChange={(e) => handleLanguageChange(e.target.value as Language)}
                                className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-2 py-1.5 text-xs text-slate-700 dark:text-slate-200 outline-none"
                            >
                                <option value="zh-CN">简体中文</option>
                                <option value="zh-TW">繁體中文</option>
                                <option value="en">English</option>
                                <option value="ja">日本語</option>
                                <option value="ko">한국어</option>
                                <option value="ar">العربية</option>
                            </select>
                        </div>

                        <div className="text-xs text-slate-500 mb-2 font-medium">{t.displayComponents}</div>
                        <div className="space-y-1 mb-4">
                            {Object.entries(visibleComponents).map(([key, isVisible]) => (
                                <button
                                    key={key}
                                    onClick={() => toggleComponent(key as any)}
                                    className={`w-full text-left px-2 py-1.5 rounded-lg text-sm transition-colors flex items-center justify-between ${isVisible ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-400/10' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5'}`}
                                >
                                    <span className="capitalize">{(t as any)[key] || key}</span>
                                    <div className={`w-2 h-2 rounded-full ${isVisible ? 'bg-blue-500 dark:bg-blue-400' : 'bg-slate-300 dark:bg-slate-600'}`}></div>
                                </button>
                            ))}
                        </div>

                        {/* Data Source Configuration */}
                         <div className="pt-2 border-t border-slate-200 dark:border-white/10 mb-2">
                            <label className="text-xs text-slate-500 my-2 block font-medium">{t.aiSource}</label>
                            <select 
                                value={aiSourceTagId} 
                                onChange={(e) => handleAiSourceChange(e.target.value)}
                                className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-2 py-1.5 text-xs text-slate-700 dark:text-slate-200 outline-none"
                            >
                                <option value="">{t.defaultSource}</option>
                                {tags.map(tag => (
                                    <option key={tag.id} value={tag.id}>{tag.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="pt-2 border-t border-slate-200 dark:border-white/10 mb-2">
                            <label className="text-xs text-slate-500 my-2 block font-medium">{t.quickDialSource}</label>
                            <select 
                                value={quickDialSourceTagId} 
                                onChange={(e) => handleQuickDialSourceChange(e.target.value)}
                                className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-2 py-1.5 text-xs text-slate-700 dark:text-slate-200 outline-none"
                            >
                                <option value="">{t.defaultSource}</option>
                                {tags.map(tag => (
                                    <option key={tag.id} value={tag.id}>{tag.name}</option>
                                ))}
                            </select>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

             {/* Skin Config Menu */}
             <AnimatePresence>
                {showSkinConfig && (
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.9, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 10 }}
                        className="absolute right-12 top-12 mt-2 w-64 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-xl shadow-2xl p-4 z-50"
                    >
                        <div className="flex justify-between items-center mb-3">
                             <div className="text-xs text-slate-500 font-medium">{t.backgroundSettings}</div>
                             <button onClick={() => setShowSkinConfig(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white"><X size={14}/></button>
                        </div>
                       
                        <div className="space-y-3">
                             <div className="mb-4">
                                <label className="text-xs text-slate-500 mb-2 block">{t.defaultStyle}</label>
                                <div className="grid grid-cols-2 gap-2">
                                    <button 
                                        onClick={() => handleStyleChange('orb')}
                                        className={`flex items-center gap-2 p-2 rounded-lg text-xs transition-colors border ${defaultBackgroundStyle === 'orb' && backgroundType === 'default' ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5'}`}
                                    >
                                        <CircleDashed size={14} /> {t.techRing}
                                    </button>
                                    <button 
                                        onClick={() => handleStyleChange('grid')}
                                        className={`flex items-center gap-2 p-2 rounded-lg text-xs transition-colors border ${defaultBackgroundStyle === 'grid' && backgroundType === 'default' ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5'}`}
                                    >
                                        <Grid size={14} /> {t.matrixGrid}
                                    </button>
                                    <button 
                                        onClick={() => handleStyleChange('rays')}
                                        className={`flex items-center gap-2 p-2 rounded-lg text-xs transition-colors border ${defaultBackgroundStyle === 'rays' && backgroundType === 'default' ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5'}`}
                                    >
                                        <Zap size={14} /> {t.dynamicRays}
                                    </button>
                                    <button 
                                        onClick={() => handleStyleChange('flow')}
                                        className={`flex items-center gap-2 p-2 rounded-lg text-xs transition-colors border ${defaultBackgroundStyle === 'flow' && backgroundType === 'default' ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5'}`}
                                    >
                                        <Activity size={14} /> {t.flowLight}
                                    </button>
                                </div>
                            </div>

                            <div className="pt-2 border-t border-slate-200 dark:border-white/10">
                                <label className="text-xs text-slate-500 mb-1.5 block">{t.customImage}</label>
                                <div className="flex gap-2">
                                    <input 
                                        type="text" 
                                        value={backgroundImage}
                                        onChange={(e) => handleBgChange(e.target.value)}
                                        placeholder="https://... "
                                        className="flex-1 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-2 py-1.5 text-xs text-slate-800 dark:text-white outline-none focus:border-blue-500"
                                    />
                                    <label className="cursor-pointer flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 hover:bg-slate-200 dark:hover:bg-white/10 transition-colors" title={t.uploadLocal}>
                                        <Upload size={14} className="text-slate-500 dark:text-slate-400" />
                                        <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                                    </label>
                                    <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
                                        {backgroundImage ? (
                                            <img src={backgroundImage} alt="preview" className="w-full h-full object-cover" />
                                        ) : (
                                            <ImageIcon size={14} className="text-slate-400" />
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="pt-2 border-t border-slate-200 dark:border-white/10">
                                <div className="text-xs text-slate-500 mb-2">{t.recommendWallpaper}</div>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&q=80',
                                        'https://images.unsplash.com/photo-1477346611705-65d1883cee1e?w=400&q=80',
                                        'https://images.unsplash.com/photo-1497436072909-60f360e1d4b0?w=400&q=80'
                                    ].map((url, idx) => (
                                        <button 
                                            key={idx}
                                            onClick={() => handleBgChange(url)}
                                            className="aspect-square rounded-lg overflow-hidden border border-slate-200 dark:border-white/10 hover:border-blue-500 transition-all relative"
                                        >
                                            <img src={url} alt="wallpaper" className="w-full h-full object-cover" />
                                            {backgroundImage === url && (
                                                <div className="absolute inset-0 bg-blue-500/30 flex items-center justify-center">
                                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                                </div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>

        {/* Content Flow */}
        <div className="flex-1 w-full flex flex-col items-center justify-center max-w-7xl pt-10">
            
            {visibleComponents.clock && (
                <motion.div layout className="mb-8 relative z-20">
                    <Clock />
                </motion.div>
            )}

            {visibleComponents.slogan && (
                <motion.div layout className="mb-6 text-center relative z-20 flex flex-col items-center">
                    <div className="flex items-center gap-3">
                        <h1 className="text-3xl md:text-5xl font-bold text-slate-800 dark:text-white tracking-tight">
                            让发现一目了然
                        </h1>
                        <span className="px-3 py-1 rounded-lg bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white font-bold text-lg md:text-xl shadow-lg transform -rotate-2">
                            YOOTags
                        </span>
                    </div>
                </motion.div>
            )}

            {visibleComponents.search && (
                <motion.div layout className="w-full mb-2 relative z-30">
                    <SearchBar placeholder={t.searchPlaceholder} />
                </motion.div>
            )}

            {visibleComponents.todo && !isTodoExpanded && todos.some(t => !t.completed) && (
                 <motion.div layout className="w-full mb-4 relative z-20">
                     <TodoTicker todos={todos} />
                 </motion.div>
            )}

            {visibleComponents.ai && (
                <motion.div layout className="w-full mb-5 relative z-20">
                    <AIPlatforms sourceTagId={aiSourceTagId} />
                </motion.div>
            )}

            {visibleComponents.quickDial && (
                <motion.div layout className="w-full mb-8 relative z-20">
                    <QuickDial sourceTagId={quickDialSourceTagId} />
                </motion.div>
            )}

        </div>

        {/* Floating Todo List - Bottom Right */}
        {visibleComponents.todo && (
            <motion.div 
                layout
                className="fixed z-40"
                drag
                dragMomentum={false}
                // Initial position if not set
                initial={todoPosition.x === 0 && todoPosition.y === 0 ? { bottom: 32, right: 32 } : { x: todoPosition.x, y: todoPosition.y }}
                animate={{ x: todoPosition.x, y: todoPosition.y }}
                onDragEnd={(_, info) => {
                    // Calculate new position
                    let newX = todoPosition.x + info.offset.x;
                    let newY = todoPosition.y + info.offset.y;

                    const newPos = {
                        x: newX,
                        y: newY
                    };
                    setTodoPosition(newPos);
                    localStorage.setItem('todo-position', JSON.stringify(newPos));
                }}
                dragConstraints={{ left: -window.innerWidth + 50, right: window.innerWidth - 50, top: -window.innerHeight + 50, bottom: window.innerHeight - 50 }}
                style={todoPosition.x === 0 && todoPosition.y === 0 ? { bottom: 32, right: 32 } : {}}
            >
                <TodoList isExpanded={isTodoExpanded} onToggleExpand={setIsTodoExpanded} todoData={todoData} />
            </motion.div>
        )}

      </div>
    </div>
  );
};

export default Home;
